import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import useGroupDetails from '@/hooks/UseGroupDetails';
import commonUtils from '@/utils/common';
import { useAppDispatch, useAppSelector } from '@/store';
import { authActions, authSelector } from '@/store/reducers/auth';
import { teacherActions, teacherSelector } from '@/store/reducers/teacher';
import {
  teacherDashboardActions,
  teacherDashboardSelector,
} from '@/store/reducers/teacher-dashboard';
import { ClassConfig, TeacherGroup, UserState } from '@/types/user';
import {
  GroupStudentProgress,
  StudentsSkillsProgress,
  StudentMetadata,
  UnitProgress,
  ClassOverviewData,
} from '@/types/progress';
import { StudentsRegisteredResponse } from '@/types/register';
import { StudentData } from '@/types/student-data';
import {
  LEVEL_CONTROL_DEFAULTS,
  hasLevelControlChanges,
  normalizeStartingLevel,
} from '@/school-management/utils/levelControl';
import { registerService } from '@/services/register';
import { trimNamePart, withTrimmedPersonNamesBatch } from '@/utils/personName';
import { progressService, userService } from '@/services';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTeacherClassesReportAggregate } from '@/features/classes-report/useTeacherClassesReportAggregate';
import { useGroupsReportClasses } from '@/features/classes-report/useGroupsReportClasses';
import {
  FilterField,
  FilterValue,
  isEmptyValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import type { CascadingFilterRequest } from '@/school-management/types/response/autocomplete';
import type {
  GroupAutocompleteItem,
  ClassCodeAutocompleteItem,
} from '@/school-management/types/response/autocomplete';
import adminCommonUtils from '@/school-management/utils/common';
import {
  normalizeGroupReportItemsToTableRows,
  classesReportTableRowToTeacherGroup,
  type ClassesReportTableRow,
} from '@/features/classes-report/normalizeGroupReportRows';
import {
  inferGroupDetailsFromStudentRows,
  studentReportItemToClassOverviewData,
} from '@/features/classes-report/studentReportToClassOverviewData';
import { fetchAllStudentsReportItemsForGroup } from '@/school-management/services/student';
import { classService } from '@/school-management/services/class';
import type { GroupsReportQueryParams } from '@/school-management/types/response';
import common from '@/utils/common';

dayjs.extend(utc);

const coTeacherIdsEqual = (a?: string[], b?: string[]) => {
  const left = [...(a ?? [])].sort();
  const right = [...(b ?? [])].sort();
  return left.length === right.length && left.every((value, index) => value === right[index]);
};

const buildTeacherIdsForClass = (currentTeacherId: string, coTeacherIds?: string[]) => {
  const uniqueCoTeachers = (coTeacherIds ?? []).filter(id => id && id !== currentTeacherId);
  return [currentTeacherId, ...uniqueCoTeachers];
};

export interface existingStudentsInterface {
  totalStudents: number;
  addedStudents: number;
  existingStudents: number;
  existingStudentsList: {
    firstName: string;
    lastName: string;
    email: string;
    error: string;
  }[];
}

const DEFAULT_MY_CLASSES_FILTERS: Record<string, FilterValue> = {
  groupName: [],
  grade: [],
  classCode: [],
};

export interface UseMyClassesInterface {
  allClassrooms: TeacherGroup[];
  filterdClassrooms: TeacherGroup[];
  classConfig: ClassConfig | null;
  /** Group id after POST create while still on the add-class form (classConfig.id stays empty). */
  activeGroupId: string;
  onCreateClass: () => void;
  createOrSaveClass: (
    newConfig: ClassConfig,
    options?: { skipAddStudentsModal?: boolean }
  ) => Promise<any>;
  onSelectClass: (classroom: TeacherGroup) => void;
  classFilters: Record<string, FilterValue>;
  setClassFilters: React.Dispatch<React.SetStateAction<Record<string, FilterValue>>>;
  classFilterFields: FilterField[];
  classCascadingFilters: CascadingFilterRequest;
  hasAnyClassFilter: boolean;
  showClassCreatedModal: boolean;
  /** Increments after "Save and add students later" create — desktop shows a success toast. */
  classCreatedWithoutStudentsNotifyTick: number;
  /** Increments after successful class deletion — desktop shows a success notification. */
  classDeletedNotifyTick: number;
  showExistingStudentsModal: existingStudentsInterface | null;
  onCloseClassCreatedModal: () => void;
  onCloseExistingStudentsModal: () => void;
  showCopyModal: boolean;
  setShowCopyModal: React.Dispatch<React.SetStateAction<boolean>>;
  onCloseCopyModal: () => void;
  onShowCopyModal: () => void;
  onAbortAddClass: () => void | Promise<void>;
  clearAddClassFlow: () => void;
  user: UserState | null;
  isSavingGroup: boolean;
  isCsvLoading: boolean;
  isCsvSending: boolean;
  setIsCsvLoading: React.Dispatch<React.SetStateAction<boolean>>;
  csvFilesAdded: boolean;
  setCsvFilesAdded: React.Dispatch<React.SetStateAction<boolean>>;
  showUploadCsvModal: boolean;
  addStudentsToNewGroup: boolean;
  showStudentsTableModal: boolean;
  onShowUploadCsvModal: (isNewGroup?: boolean) => void;
  onCloseUploadCsvModal: () => void;
  onShowStudentsTableModal: () => void;
  onBackStudentsTableModal: () => void;
  studentsData: StudentData[];
  setStudentsData: React.Dispatch<React.SetStateAction<StudentData[]>>;
  onCsvStudentsCreate: () => Promise<void>;
  disableSubmitButton: boolean;
  setDisableSubmitButton: React.Dispatch<React.SetStateAction<boolean>>;
  showNewStudentInput: boolean;
  setShowNewStudentInput: React.Dispatch<React.SetStateAction<boolean>>;
  classStudentsExtraDataResponse: Map<string, StudentsRegisteredResponse>;
  currentClassroom: TeacherGroup | null;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  fetchDataError: { status?: number; message?: string } | null;
  refreshStudentsData: () => void;
  studentsProgress: ClassOverviewData[];
  statistics: StudentsSkillsProgress | null;
  selectedStudent: ClassOverviewData | null;
  isClassEdit: boolean;
  setIsClassEdit: React.Dispatch<React.SetStateAction<boolean>>;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  onShowStudentModal: (studentId: string) => Promise<void>;
  onHideStudentModal: () => void;
  isStudentDataLoading: boolean;
  showStudentDataModal: boolean;
  selectedStudentData: StudentMetadata | null;
  showStudentEditModal: boolean;
  onShowStudentEditModal: () => void;
  onCloseStudentEditModal: () => void;
  editStudent: (
    firstName: string,
    lastName: string,
    language: string,
    level?: number
  ) => Promise<void>;
  showUnitData: boolean;
  onShowUnitData: (unitId: number) => void;
  onHideUnitData: () => void;
  selectedUnit: UnitProgress | undefined | null;
  showCsvStudentsCreateError: boolean;
  setShowCsvStudentsCreateError: React.Dispatch<React.SetStateAction<boolean>>;
  onStudentDelete: (reason: string) => void;
  isStudentDeleting: boolean;
  showStudentDeleteError: boolean;
  setShowStudentDeleteError: React.Dispatch<React.SetStateAction<boolean>>;
  isClassDeleting: boolean;
  showClassDeleteError: boolean;
  setShowClassDeleteError: React.Dispatch<React.SetStateAction<boolean>>;
  onClassDelete: (reason?: string, classId?: string) => void;
  groupDetailsFromApi: {
    additionalTeacherName?: {
      id: string;
      firstName: string;
      lastName: string;
    };
  } | null;
  isClassesListLoading: boolean;
  classesListError: string | null;
  filteredClassesReportRows: ClassesReportTableRow[];
  refetchClassesList: () => Promise<void>;
  /** Desktop: same data/shape as admin classes table (null on mobile). */
  desktopClassesReport: null | {
    tableData: ClassesReportTableRow[];
    currentPage: number;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSortChange: (newSortBy: string | null, newSortDirection: 'asc' | 'desc' | null) => void;
    isLoadingClasses: boolean;
    isFirstLoading: boolean;
    classesError: string | null;
    paginationInfo: { totalPages: number; totalCount: number };
    buildClassQueryParams: (page: number) => GroupsReportQueryParams;
    refetch: (page: number) => Promise<void>;
    searchQuery: string;
    setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  };
}

const LAST_SELECTED_CLASS_LOCAL_STORAGE_KEY = 'last_selected_class';
const TEACHER_CLASSES_FILTERS_STORAGE_KEY_PREFIX = 'teacher_classes_filters_';

const UseMyClasses = (isMobile: boolean): UseMyClassesInterface => {
  const { t } = useAdminTranslation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(authSelector);
  const organizationId = user?.metadata?.organizationDetails?.organizationId;
  const teacherUserId = user?.id;
  const { isNew, selectedClassId } = useAppSelector(teacherDashboardSelector);
  const groupDetails = useGroupDetails();
  const [classFilters, setClassFilters] = useState<Record<string, FilterValue>>(
    DEFAULT_MY_CLASSES_FILTERS
  );
  const [classConfig, setClassConfig] = useState<ClassConfig | null>(null);
  const [pendingCreatedGroupId, setPendingCreatedGroupId] = useState<string>('');
  const [prevClassConfig, setPrevClassConfig] = useState<ClassConfig | null>(null);
  const activeGroupId = pendingCreatedGroupId || classConfig?.id || '';
  const [currentClassroom, setCurrentClassroom] = useState<TeacherGroup | null>(null);
  const [showClassCreatedModal, setShowClassCreatedModal] = useState(false);
  const [classCreatedWithoutStudentsNotifyTick, setClassCreatedWithoutStudentsNotifyTick] =
    useState(0);
  const [classDeletedNotifyTick, setClassDeletedNotifyTick] = useState(0);
  const [optimisticallyDeletedClassIds, setOptimisticallyDeletedClassIds] = useState<Set<string>>(
    new Set()
  );
  const [showExistingStudentsModal, setShowExistingStudentsModal] =
    useState<existingStudentsInterface | null>(null);
  const [showUploadCsvModal, setShowUploadCsvModal] = useState(false);
  const [addStudentsToNewGroup, setAddStudentsToNewGroup] = useState(false);
  const [showStudentsTableModal, setShowStudentsTableModal] = useState(false);
  const [isCsvLoading, setIsCsvLoading] = useState(false);
  const [isCsvSending, setIsCsvSending] = useState(false);
  const [csvFilesAdded, setCsvFilesAdded] = useState<boolean>(false);
  const [showCsvStudentsCreateError, setShowCsvStudentsCreateError] = useState<boolean>(false);

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [studentsData, setStudentsData] = useState<StudentData[]>([]);
  const [classStudentsExtraDataResponse, setClassStudentsExtraDataResponse] = useState<
    Map<string, StudentsRegisteredResponse>
  >(new Map<string, StudentsRegisteredResponse>());
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [showNewStudentInput, setShowNewStudentInput] = useState(false);

  const { students: studentsProgress } = useAppSelector(teacherSelector);
  const [statistics, setStatistics] = useState<StudentsSkillsProgress | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchDataError, setFetchDataError] = useState<{
    status?: number;
    message?: string;
  } | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<GroupStudentProgress | null>(null);
  const [isClassEdit, setIsClassEdit] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [groupDetailsFromApi, setGroupDetailsFromApi] = useState<{
    additionalTeacherName?: {
      id: string;
      firstName: string;
      lastName: string;
    };
  } | null>(null);
  const selectedClassIdRef = useRef<string | null>(
    localStorage.getItem(LAST_SELECTED_CLASS_LOCAL_STORAGE_KEY) || null
  );
  const classesFiltersRestoredRef = useRef(false);

  const [isStudentDataLoading, setIsStudentDataLoading] = useState<boolean>(false);
  const [showStudentDataModal, setShowStudentDataModal] = useState<boolean>(false);
  const [selectedStudentData, setSelectedStudentData] = useState<StudentMetadata | null>(null);
  const [showStudentEditModal, setShowStudentEditModal] = useState<boolean>(false);

  const [showUnitData, setShowUnitData] = useState<boolean>(false);
  const [selectedUnit, setSelectedUnit] = useState<UnitProgress | undefined | null>();
  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const classFilterFields: FilterField[] = useMemo(() => {
    const allGrades = adminCommonUtils.getAllGradesForDropdown(t);
    const gradeNames = [...allGrades.map(g => g.label)];
    const isClassCodeEnabled = common.isClassCode(user);

    return [
      {
        key: 'groupName',
        label: t('genericTable.classes.name'),
        options: [],
        multi: true,
        autocompleteType: 'groups',
        searchPlaceholder:
          t('schoolDetailsPage.classes.filters.classSearchPlaceholder') || 'Search class',
      },
      ...(isClassCodeEnabled
        ? [
            {
              key: 'classCode',
              label: t('schoolDetailsPage.classes.filters.classCode'),
              options: [],
              multi: true,
              autocompleteType: 'class_code',
              searchPlaceholder:
                t('schoolDetailsPage.classes.filters.classCodePlaceholder') || 'Search class code',
            },
          ]
        : []),
      {
        key: 'grade',
        label: t('genericTable.classes.grade'),
        options: gradeNames,
        multi: true,
        searchPlaceholder:
          t('schoolDetailsPage.classes.filters.gradeSearchPlaceholder') || 'Search grade',
      },
    ] as FilterField[];
  }, [t, user]);

  const gradeIdMapper = useMemo(() => adminCommonUtils.getGradesLabelToIdMapper(t), [t]);

  const toGroupIds = useCallback((value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as GroupAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.groupId) : undefined;
  }, []);

  const toGradeIds = useCallback(
    (value: FilterValue): number[] | undefined => {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      if (arr.length === 0) return undefined;
      const ids = arr.map(label => gradeIdMapper[label]).filter((id): id is number => id != null);
      return ids.length > 0 ? ids : undefined;
    },
    [gradeIdMapper]
  );

  const toClassCodeIds = useCallback((value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as ClassCodeAutocompleteItem[]) : [];
    return arr.length > 0 ? arr : undefined;
  }, []);

  const classCascadingFilters = useMemo((): CascadingFilterRequest => {
    return {
      teacherIds: teacherUserId ? [teacherUserId] : undefined,
      groupIds: toGroupIds(classFilters.groupName),
      gradeIds: toGradeIds(classFilters.grade),
      classCodes: toClassCodeIds(classFilters.classCode),
    };
  }, [classFilters, toGroupIds, toGradeIds, toClassCodeIds, teacherUserId]);

  const hasAnyClassFilter = useMemo(
    () =>
      classFilterFields.some(f => {
        return f && classFilters[f.key] ? !isEmptyValue(f, classFilters[f.key]) : false;
      }),
    [classFilterFields, classFilters]
  );

  useEffect(() => {
    classesFiltersRestoredRef.current = false;
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    if (!classesFiltersRestoredRef.current) {
      classesFiltersRestoredRef.current = true;
      try {
        const stored = sessionStorage.getItem(
          `${TEACHER_CLASSES_FILTERS_STORAGE_KEY_PREFIX}${organizationId}`
        );
        if (stored) {
          const parsed = JSON.parse(stored) as Record<string, FilterValue>;
          if (parsed && typeof parsed === 'object') {
            setClassFilters(prev => ({ ...DEFAULT_MY_CLASSES_FILTERS, ...prev, ...parsed }));
          }
        }
      } catch {
        // ignore invalid stored filters
      }
    }
  }, [organizationId]);

  useEffect(() => {
    if (!organizationId) return;
    try {
      sessionStorage.setItem(
        `${TEACHER_CLASSES_FILTERS_STORAGE_KEY_PREFIX}${organizationId}`,
        JSON.stringify(classFilters)
      );
    } catch {
      // ignore quota / serialization errors
    }
  }, [organizationId, classFilters]);

  const {
    rawItems,
    isLoading: aggregateLoading,
    error: aggregateError,
    refetch: refetchAggregate,
  } = useTeacherClassesReportAggregate({
    organizationId,
    teacherUserId,
    translateForGradeMapper: t,
    failedToFetchMessage: t('schoolDetailsPage.classes.failedToFetch') || '',
    enabled: isMobile,
    classFilters,
  });

  const [desktopSearchQuery, setDesktopSearchQuery] = useState('');
  const {
    currentPage: desktopCurrentPage,
    setCurrentPage: setDesktopCurrentPage,
    sortBy: desktopSortBy,
    sortDirection: desktopSortDirection,
    onSortChange: desktopOnSortChange,
    isLoadingClasses: desktopLoadingClasses,
    isFirstLoading: desktopFirstLoading,
    classesError: desktopClassesError,
    fetchedClasses: desktopFetchedClasses,
    paginationInfo: desktopPaginationInfo,
    refetch: desktopRefetchPage,
    buildClassQueryParams: desktopBuildClassQueryParams,
  } = useGroupsReportClasses({
    organizationId: organizationId || '',
    translateForGradeMapper: t,
    lockedTeacherIds: teacherUserId ? [teacherUserId] : [],
    classFilters,
    enabled: !isMobile && !!organizationId && !!teacherUserId,
    failedToFetchMessage: t('schoolDetailsPage.classes.failedToFetch') || '',
    searchQuery: desktopSearchQuery,
  });

  const mobileReportRows = useMemo(() => {
    if (!isMobile) {
      return [];
    }
    return normalizeGroupReportItemsToTableRows(rawItems, t).filter(
      row => !optimisticallyDeletedClassIds.has(row.id)
    );
  }, [isMobile, rawItems, t, optimisticallyDeletedClassIds]);

  const desktopTableData = useMemo(() => {
    if (isMobile) {
      return [];
    }
    return normalizeGroupReportItemsToTableRows(desktopFetchedClasses, t).filter(
      row => !optimisticallyDeletedClassIds.has(row.id)
    );
  }, [isMobile, desktopFetchedClasses, t, optimisticallyDeletedClassIds]);

  const allClassrooms = useMemo(() => {
    if (!isMobile) {
      return [];
    }
    return [...mobileReportRows.map(classesReportTableRowToTeacherGroup)].sort((group1, group2) => {
      const name1 = (group1.name || '').toUpperCase();
      const name2 = (group2.name || '').toUpperCase();

      if (name1 < name2) {
        return -1;
      }

      if (name1 > name2) {
        return 1;
      }

      return 0;
    });
  }, [isMobile, mobileReportRows]);

  const filterdClassrooms = useMemo(() => {
    if (!isMobile) {
      return desktopTableData.map(classesReportTableRowToTeacherGroup);
    }
    return allClassrooms;
  }, [isMobile, desktopTableData, allClassrooms]);

  const filteredClassesReportRows = useMemo(() => {
    if (!isMobile) {
      return desktopTableData;
    }
    const allowed = new Set(filterdClassrooms.map(g => g.id));
    return mobileReportRows.filter(r => allowed.has(r.id));
  }, [isMobile, desktopTableData, mobileReportRows, filterdClassrooms]);

  const isClassesListLoading = isMobile
    ? aggregateLoading
    : desktopFirstLoading || desktopLoadingClasses;
  const classesListError = isMobile ? aggregateError : desktopClassesError;

  const refetchClassesList = useCallback(async () => {
    if (isMobile) {
      await refetchAggregate();
    } else {
      await desktopRefetchPage(desktopCurrentPage);
    }
  }, [isMobile, refetchAggregate, desktopRefetchPage, desktopCurrentPage]);

  const desktopClassesReport = useMemo(() => {
    if (isMobile) {
      return null;
    }
    return {
      tableData: desktopTableData,
      currentPage: desktopCurrentPage,
      setCurrentPage: setDesktopCurrentPage,
      sortBy: desktopSortBy,
      sortDirection: desktopSortDirection,
      onSortChange: desktopOnSortChange,
      isLoadingClasses: desktopLoadingClasses,
      isFirstLoading: desktopFirstLoading,
      classesError: desktopClassesError,
      paginationInfo: desktopPaginationInfo,
      buildClassQueryParams: desktopBuildClassQueryParams,
      refetch: desktopRefetchPage,
      searchQuery: desktopSearchQuery,
      setSearchQuery: setDesktopSearchQuery,
    };
  }, [
    isMobile,
    desktopTableData,
    desktopCurrentPage,
    setDesktopCurrentPage,
    desktopSortBy,
    desktopSortDirection,
    desktopOnSortChange,
    desktopLoadingClasses,
    desktopFirstLoading,
    desktopClassesError,
    desktopPaginationInfo,
    desktopBuildClassQueryParams,
    desktopRefetchPage,
    desktopSearchQuery,
    setDesktopSearchQuery,
  ]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const onShowCopyModal = () => {
    scrollToTop();
    setShowCopyModal(true);
  };

  const clearAddClassFlow = () => {
    setPendingCreatedGroupId('');
    setClassConfig(null);
    dispatch(teacherDashboardActions.onReset());
  };

  const onAbortAddClass = async () => {
    scrollToTop();

    if (pendingCreatedGroupId && user) {
      try {
        await userService.deleteClass(user, pendingCreatedGroupId, '');
      } catch (error) {
        console.log('deleteClass error', error);
      }
      setPendingCreatedGroupId('');
    }

    const prev = prevClassConfig;
    if (prev) {
      setClassConfig(prev);
      setPrevClassConfig(null);
      if (prev.id) {
        dispatch(teacherDashboardActions.onEdit(prev.id));
      } else {
        dispatch(teacherDashboardActions.onReset());
      }
    } else {
      setClassConfig(null);
      dispatch(teacherDashboardActions.onReset());
    }
    setIsClassEdit(false);
  };

  const onCloseCopyModal = () => {
    setShowCopyModal(false);
  };

  const onCloseClassCreatedModal = () => {
    setShowClassCreatedModal(false);
    setPendingCreatedGroupId('');
    setClassConfig(null);
    setIsClassEdit(false);
    dispatch(teacherDashboardActions.onReset());
  };

  const onCloseExistingStudentsModal = () => {
    setShowExistingStudentsModal(null);
  };

  const onShowUploadCsvModal = (isNewGroup: boolean = false) => {
    scrollToTop();
    setAddStudentsToNewGroup(isNewGroup);
    setShowUploadCsvModal(true);
  };

  const onCloseUploadCsvModal = () => {
    setShowUploadCsvModal(false);
    setCsvFilesAdded(false);
  };

  const onShowStudentsTableModal = () => {
    scrollToTop();
    setShowUploadCsvModal(false);
    setShowStudentsTableModal(true);
  };

  const onBackStudentsTableModal = () => {
    setCsvFilesAdded(false);
    setStudentsData([]);
    setShowStudentsTableModal(false);
    setShowUploadCsvModal(true);
  };

  const createOrSaveClass = async (
    newConfig: ClassConfig,
    options?: { skipAddStudentsModal?: boolean }
  ) => {
    const organizationId = user?.metadata?.organizationDetails?.organizationId;
    const currentTeacherId = user?.metadata?.id;

    const notifyClassSaveFailed = (error: unknown, context: 'create' | 'update') => {
      console.error(`Failed to save class (${context}):`, error);
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
    };

    const syncClassDetails = async (groupId: string) => {
      if (!organizationId || !currentTeacherId) {
        return;
      }

      const gradeId = String(newConfig.grade ?? '1');

      await classService.updateClass(organizationId, groupId, {
        groupName: String(newConfig.name ?? '').trim(),
        gradeId,
        teacherIds: buildTeacherIdsForClass(currentTeacherId, newConfig.coTeacherIds),
        placementTestRequired:
          newConfig.placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired,
        defaultStartingLevel: newConfig.placementTestRequired
          ? null
          : newConfig.defaultStartingLevel ?? null,
        allowIndividualAdvancement:
          newConfig.allowIndividualAdvancement ?? LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement,
      });
    };

    // ELL-5901 rollback
    // is a class with that name already exists
    // if (
    //   (allClassrooms || []).filter(c => c.id !== classConfig?.id && c.name === newConfig.name)
    //     .length > 0
    // ) {
    //   throw new Error('editClassClassNameAlreadyExists');
    // }

    const existingGroupId = pendingCreatedGroupId || classConfig?.id;

    // Update group
    if (existingGroupId) {
      const loadedLevelControl = {
        placementTestRequired:
          classConfig?.placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired,
        defaultStartingLevel:
          classConfig?.defaultStartingLevel ?? LEVEL_CONTROL_DEFAULTS.defaultStartingLevel,
        allowIndividualAdvancement:
          classConfig?.allowIndividualAdvancement ??
          LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement,
      };
      const nextLevelControl = {
        placementTestRequired:
          newConfig.placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired,
        defaultStartingLevel: newConfig.placementTestRequired
          ? null
          : newConfig.defaultStartingLevel ?? null,
        allowIndividualAdvancement:
          newConfig.allowIndividualAdvancement ?? LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement,
      };

      const hasDetailsChange =
        newConfig.grade !== classConfig?.grade ||
        newConfig.name !== classConfig?.name ||
        !coTeacherIdsEqual(newConfig.coTeacherIds, classConfig?.coTeacherIds) ||
        hasLevelControlChanges(loadedLevelControl, nextLevelControl);

      if (hasDetailsChange) {
        try {
          await syncClassDetails(existingGroupId);

          setClassConfig(prev => {
            return {
              ...prev,
              name: newConfig?.name,
              grade: newConfig?.grade,
              coTeacherIds: newConfig.coTeacherIds ?? [],
              ...nextLevelControl,
            };
          });

          scrollToTop();
          void refetchClassesList();
        } catch (e) {
          notifyClassSaveFailed(e, 'update');
        }
      }

      if (pendingCreatedGroupId) {
        if (options?.skipAddStudentsModal) {
          scrollToTop();
          setPendingCreatedGroupId('');
          setClassConfig(null);
          dispatch(teacherDashboardActions.onReset());
          setIsClassEdit(false);
          setClassCreatedWithoutStudentsNotifyTick(n => n + 1);
        } else if (!currentClassroom?.classCode && !classConfig?.classCode) {
          onShowUploadCsvModal(true);
        }
      }

      return;
    } else {
      // create new group
      try {
        const newGroup = await groupDetails.createGroup({
          groupName: newConfig.name ?? '',
          grade: newConfig.grade ?? '',
          placementTestRequired:
            newConfig.placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired,
          defaultStartingLevel: newConfig.placementTestRequired
            ? null
            : newConfig.defaultStartingLevel ?? null,
          allowIndividualAdvancement:
            newConfig.allowIndividualAdvancement ??
            LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement,
        });

        setPrevClassConfig(null);
        dispatch(teacherDashboardActions.onEdit(newGroup?.groupId ?? ''));
        selectedClassIdRef.current = newGroup?.groupId ?? '';

        if (selectedClassIdRef.current) {
          localStorage.setItem(LAST_SELECTED_CLASS_LOCAL_STORAGE_KEY, selectedClassIdRef.current);
        } else {
          localStorage.removeItem(LAST_SELECTED_CLASS_LOCAL_STORAGE_KEY);
        }

        setGroupDetailsFromApi(null);

        setCurrentClassroom({
          id: newGroup?.groupId || '',
          name: newGroup?.groupData?.groupName || '',
          grade: newGroup?.groupData?.grade || '',
          totalStudents: 0,
          groupLevel: '',
          invitationLinkForStudents: '',
          ...(newGroup?.classCode && { classCode: newGroup?.classCode }),
        });

        setPendingCreatedGroupId(newGroup?.groupId ?? '');
        setClassConfig(prev => {
          return {
            ...prev,
            name: newConfig?.name,
            grade: newConfig?.grade,
            coTeacherIds: newConfig.coTeacherIds ?? [],
            invitationLinkForStudents: newGroup?.invitationLink ?? '',
            levelTest: newConfig?.levelTest ?? false,
            placementTestRequired:
              newConfig.placementTestRequired ?? LEVEL_CONTROL_DEFAULTS.placementTestRequired,
            defaultStartingLevel: newConfig.placementTestRequired
              ? null
              : newConfig.defaultStartingLevel ?? null,
            allowIndividualAdvancement:
              newConfig.allowIndividualAdvancement ??
              LEVEL_CONTROL_DEFAULTS.allowIndividualAdvancement,
          };
        });
        if (newGroup?.groupId && (newConfig.coTeacherIds?.length ?? 0) > 0) {
          try {
            await syncClassDetails(newGroup.groupId);
          } catch (e) {
            notifyClassSaveFailed(e, 'create');
          }
        }
        fetchStudentsData(user, newGroup?.groupId ?? null);
        if (newGroup && !newGroup.classCode && !options?.skipAddStudentsModal) {
          onShowUploadCsvModal(true);
        }
        if (options?.skipAddStudentsModal) {
          scrollToTop();
          setPendingCreatedGroupId('');
          setClassConfig(null);
          dispatch(teacherDashboardActions.onReset());
          setIsClassEdit(false);
          setClassCreatedWithoutStudentsNotifyTick(n => n + 1);
        }
        void refetchClassesList();
        return newGroup;
      } catch (e) {
        console.error('Failed to create class:', e);
        commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
      }
    }
  };

  const onCreateClass = () => {
    setPendingCreatedGroupId('');
    setClassConfig(prev => {
      setPrevClassConfig(prev);

      const classConfigObj = {
        id: '',
        name: '',
        grade: '',
        groupLevel: '',
        invitationLinkForStudents: '',
        levelTest: true,
        classCode: null,
        ...LEVEL_CONTROL_DEFAULTS,
      } as ClassConfig;

      return classConfigObj;
    });

    if (!isNew) {
      dispatch(teacherDashboardActions.onAdd());
    }
  };

  const onSelectClass = (classroom: TeacherGroup | null) => {
    if (!classroom) {
      setCurrentClassroom(null);
      setClassConfig(null);
      setStatistics(null);
      dispatch(teacherActions.setStudents([]));
      dispatch(teacherDashboardActions.onReset());
      localStorage.removeItem(LAST_SELECTED_CLASS_LOCAL_STORAGE_KEY);
      return;
    }

    const {
      id,
      name,
      grade,
      groupLevel,
      invitationLinkForStudents,
      classCode,
      placementTestRequired,
      defaultStartingLevel,
      allowIndividualAdvancement,
    } = classroom;

    selectedClassIdRef.current = id;
    if (selectedClassIdRef.current) {
      localStorage.setItem(LAST_SELECTED_CLASS_LOCAL_STORAGE_KEY, selectedClassIdRef.current);
    } else {
      localStorage.removeItem(LAST_SELECTED_CLASS_LOCAL_STORAGE_KEY);
    }

    setCurrentClassroom(classroom);
    setClassConfig({
      id,
      name,
      grade,
      groupLevel,
      invitationLinkForStudents,
      ...(classCode && { classCode }),
      placementTestRequired,
      defaultStartingLevel: normalizeStartingLevel(defaultStartingLevel),
      allowIndividualAdvancement,
    });
    setGroupDetailsFromApi(null);
    const teacherGroups = user?.metadata.organizationDetails.teacher.teacherGroups || [];
    const targetGroup = teacherGroups.find(group => group.id === id);
    const studentsAmount = classroom.totalStudents ?? targetGroup?.totalStudents ?? 0;
    fetchStudentsData(user, id);
    if (studentsAmount === 0) {
      dispatch(teacherActions.setStudents([]));
      setStatistics(null);
    }

    if (!selectedClassId) {
      dispatch(teacherDashboardActions.onEdit(classroom.id));
    }
  };

  const sanitizeString = (value: string) =>
    value
      .replace(/[\u200B-\u200D\uFEFF\u202A-\u202E\u2066-\u2069]/g, '') // Remove zero-width and directional marks
      .replace(/\s+/g, '') // Remove all whitespace
      .trim();

  const onCsvStudentsCreate = async () => {
    setIsCsvSending(true);
    try {
      // Sanitize all emails before sending to backend
      const sanitizedStudentsData = withTrimmedPersonNamesBatch(
        studentsData.map(student => ({
          ...student,
          email: sanitizeString(student.email),
        }))
      );

      const createCsvStudentsResponse = await registerService.createCsvStudents(
        user,
        activeGroupId,
        sanitizedStudentsData
      );
      dispatch(
        authActions.updatedClassroomStudentsAmount({
          id: activeGroupId,
          amount: createCsvStudentsResponse?.data.data.registeredStudents.length ?? 0,
        })
      );

      const addedStudents = createCsvStudentsResponse?.data?.data?.registeredStudents?.length || 0;
      const existingStudents =
        createCsvStudentsResponse?.data?.data?.unregisteredStudents?.length || 0;
      const totalStudents = studentsData?.length || 0;

      setStudentsData([]);
      setCsvFilesAdded(false);
      setShowStudentsTableModal(false);

      if (existingStudents > 0) {
        setShowExistingStudentsModal({
          addedStudents: addedStudents,
          existingStudents: existingStudents,
          totalStudents: totalStudents,
          existingStudentsList: createCsvStudentsResponse
            ? createCsvStudentsResponse.data.data.unregisteredStudents.map(student => {
                return {
                  firstName: student.firstName,
                  lastName: student.lastName,
                  email: student.email,
                  error: student.error,
                };
              })
            : [],
        });
      } else {
        setShowClassCreatedModal(true);
      }
    } catch (err) {
      console.error('Error creating CSV students:', err);
      setShowCsvStudentsCreateError(true);
    } finally {
      setIsCsvSending(false);
      fetchStudentsData(user, activeGroupId);
      void refetchClassesList();
    }
  };

  const fetchClassIdRef = useRef<string | null>();

  const fetchClassProgress = async (user: UserState | null, classId: string | null) => {
    if (!(classId && user && organizationId)) {
      return;
    }

    try {
      const reportItems = await fetchAllStudentsReportItemsForGroup(organizationId, classId);

      if (fetchClassIdRef.current !== classId) {
        return;
      }

      setGroupDetailsFromApi(inferGroupDetailsFromStudentRows(reportItems, user.metadata?.id));

      const mappedStudents = reportItems.reduce<{
        students: ClassOverviewData[];
        validStudents: number;
        levelSum: number;
        scoreSum: number;
        progressSum: number;
      }>(
        (acc, row) => {
          const base = studentReportItemToClassOverviewData(row);
          const activitiesProgressInt = Math.round(base.progress.activitiesProgress);

          acc.students.push({
            ...base,
            progress: {
              ...base.progress,
              activitiesProgress: activitiesProgressInt,
            },
          });

          if (base.level) {
            acc.validStudents++;
            acc.levelSum += base.level;
            acc.scoreSum += base.progress.aVGScore ?? 0;
            acc.progressSum += activitiesProgressInt;
          }

          return acc;
        },
        {
          students: [],
          validStudents: 0,
          levelSum: 0,
          scoreSum: 0,
          progressSum: 0,
        }
      );

      dispatch(teacherActions.setStudents(mappedStudents.students));
      setStatistics(
        mappedStudents.validStudents
          ? {
              level: commonUtils.roundNumber(
                mappedStudents.levelSum / mappedStudents.validStudents,
                0
              ),
              score: commonUtils.roundNumber(
                mappedStudents.scoreSum / mappedStudents.validStudents,
                2
              ),
              progress: commonUtils.roundNumber(
                mappedStudents.progressSum / mappedStudents.validStudents,
                2
              ),
            }
          : null
      );
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const fetchStudentsData = async (user: UserState | null, classId: string | null) => {
    setIsLoading(true);
    setFetchDataError(null);
    fetchClassIdRef.current = classId;

    const result = await Promise.allSettled([fetchClassProgress(user, classId)]);

    if (fetchClassIdRef.current !== classId) {
      return;
    }

    if (result[0].status === 'rejected') {
      const error = result[0].reason;
      const axiosError = error as any;
      const status = axiosError?.response?.status;
      const responseData = axiosError?.response?.data;
      const message = responseData?.message;

      setFetchDataError({
        status,
        message,
      });
      dispatch(teacherActions.setStudents([]));
      setStatistics(null);
      setGroupDetailsFromApi(null);
    }

    setIsLoading(false);
  };

  const refreshStudentsData = () => {
    fetchStudentsData(user, activeGroupId || null);
  };

  const classesSelectionInitializedRef = useRef(false);

  useEffect(() => {
    classesSelectionInitializedRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (allClassrooms.length === 0) {
      classesSelectionInitializedRef.current = false;
    }
  }, [allClassrooms.length]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }
    if (isNew) {
      onCreateClass();
      return;
    }
    if (isClassesListLoading) {
      return;
    }
    if (classesSelectionInitializedRef.current) {
      return;
    }
    if (allClassrooms.length === 0) {
      return;
    }

    const classToEdit = selectedClassIdRef.current
      ? allClassrooms.find(group => group.id === selectedClassIdRef.current)
      : allClassrooms[0];

    if (classToEdit) {
      onSelectClass(classToEdit);
      classesSelectionInitializedRef.current = true;
    }
  }, [isMobile, isNew, isClassesListLoading, allClassrooms, user]);

  const onShowStudentModal = async (studentId: string) => {
    setShowStudentDataModal(true);
    const student = studentsProgress.find(s => s.id === studentId);
    setSelectedStudent(student ?? null);
  };

  const onHideStudentModal = () => {
    setShowStudentDataModal(false);
    onCloseStudentEditModal();
    setSelectedStudent(null);
    onHideUnitData();
    setSelectedUnit(null);
  };

  const onShowStudentEditModal = () => {
    setShowStudentEditModal(true);
  };

  const onCloseStudentEditModal = () => {
    setShowStudentEditModal(false);
  };

  const editStudent = async (
    firstName: string,
    lastName: string,
    language: string,
    level?: number
  ) => {
    if (!selectedStudent) {
      return;
    }

    const trimmedFirstName = trimNamePart(firstName);
    const trimmedLastName = trimNamePart(lastName);

    const hasNameOrLanguageChanged =
      trimmedFirstName !== selectedStudent.firstName ||
      trimmedLastName !== selectedStudent.lastName ||
      language !== selectedStudent.languagePreferences?.interfaceLanguage;

    const hasLevelChanged =
      level !== undefined && level !== selectedStudent.level && selectedStudent.level !== undefined;

    if (!hasNameOrLanguageChanged && !hasLevelChanged) {
      return;
    }

    setIsStudentDataLoading(true);

    try {
      if (selectedStudent.id) {
        if (hasNameOrLanguageChanged) {
          const response = (
            await userService.updateStudentSettings(
              user,
              selectedStudent.id,
              trimmedFirstName,
              trimmedLastName,
              language
            )
          )?.data.data;

          if (!response) {
            throw new Error('Failed to update student settings');
          }
        }

        if (hasLevelChanged) {
          const levelString = level!.toString();
          const results = await userService.updateStudentLevel(
            user,
            selectedStudent.id,
            levelString
          );

          if (results?.data.data === false) {
            throw new Error('Failed to update student level');
          }
        }

        setSelectedStudent(prevState => {
          if (!prevState) return prevState;

          const prevLanguagePreferences = prevState.languagePreferences!;
          return {
            ...prevState,
            firstName: trimmedFirstName,
            lastName: trimmedLastName,
            ...(level !== undefined && prevState?.level !== undefined ? { level } : {}),
            languagePreferences: {
              ...prevLanguagePreferences,
              interfaceLanguage: language,
              learningLanguage: prevLanguagePreferences.learningLanguage!,
              nativeLanguage: prevLanguagePreferences.nativeLanguage!,
              fromToLanguageParams: prevLanguagePreferences.fromToLanguageParams!,
            },
          };
        });

        setSelectedStudentData(prevState => {
          return prevState
            ? {
                ...prevState,
                studentData: {
                  ...prevState.studentData,
                  firstName: trimmedFirstName,
                  lastName: trimmedLastName,
                  interfaceLanguage: language,
                  ...(level !== undefined ? { level } : {}),
                },
              }
            : prevState;
        });
        fetchStudentsData(user, classConfig?.id ?? '');
      }

      setIsStudentDataLoading(false);
    } catch (error) {
      setIsStudentDataLoading(false);
      throw error;
    }
  };

  useEffect(() => {
    if (!selectedStudent) {
      return;
    }

    const fetchProgress = async () => {
      setIsStudentDataLoading(true);
      if (
        selectedStudent.level &&
        selectedStudent?.userStatus !== 'INACTIVE' &&
        selectedStudent?.userStatus !== 'LEVEL_TEST_INCOMPLETE'
      ) {
        try {
          const response = (
            await progressService.fetchStudentProgress(
              user,
              selectedStudent.id,
              selectedStudent.level
            )
          )?.data.data;

          if (response) {
            setSelectedStudentData(response);
          }
        } catch (error) {
          console.log(error);
          commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
          setShowStudentDataModal(false);
        } finally {
          setIsStudentDataLoading(false);
        }
      } else {
        setSelectedStudentData(null);
        setIsStudentDataLoading(false);
      }
    };

    fetchProgress();
  }, [selectedStudent]);

  const onShowUnitData = (unitId: number) => {
    const unit = selectedStudentData?.progress.unitsProgress.find(unit => unit.unitId === unitId);
    setSelectedUnit(unit);
    setShowUnitData(!!unit);
  };

  const onHideUnitData = () => {
    setShowUnitData(false);
  };

  const [isStudentDeleting, setIsStudentDeleting] = useState<boolean>(false);
  const [showStudentDeleteError, setShowStudentDeleteError] = useState<boolean>(false);
  const onStudentDelete = async (reason: string) => {
    if (!user || !classConfig?.id || !selectedStudent || isStudentDeleting) {
      return;
    }

    setIsStudentDeleting(true);

    let isDeleted: boolean | undefined = false;
    try {
      isDeleted = (await userService.deleteStudent(user, selectedStudent.id, reason))?.data.data;
    } catch (error) {
      console.log('deleteStudent error', error);
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
    }

    if (!isDeleted) {
      setShowStudentDeleteError(true);
      setIsStudentDeleting(false);
      return;
    }

    const classId = classConfig.id;

    try {
      dispatch(
        authActions.updatedClassroomStudentsAmount({
          id: classId,
          amount: -1,
        })
      );
      fetchStudentsData(user, classId);
    } catch (error) {
      console.log(error);
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
    } finally {
      onHideStudentModal();
      setIsStudentDeleting(false);
    }
  };

  const [isClassDeleting, setIsClassDeleting] = useState<boolean>(false);
  const [showClassDeleteError, setShowClassDeleteError] = useState<boolean>(false);
  const onClassDelete = async (reason?: string, targetClassId?: string) => {
    const classId = targetClassId || classConfig?.id;
    if (!user || !classId || isClassDeleting) {
      return;
    }

    setIsClassDeleting(true);
    let isDeleted: boolean | undefined = false;
    try {
      isDeleted = (await userService.deleteClass(user, classId, reason || ''))?.data.data;
    } catch (error) {
      console.log('deleteClass error', error);
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
    }

    if (!isDeleted) {
      setShowClassDeleteError(true);
      setIsClassDeleting(false);
      return;
    }

    try {
      dispatch(teacherDashboardActions.removeGroup(classId));
      dispatch(authActions.removeClass(classId));
      setOptimisticallyDeletedClassIds(prev => {
        const next = new Set(prev);
        next.add(classId);
        return next;
      });
      // Group deletion is eventually consistent in reports; wait briefly before refreshing.
      await delay(1200);
      await refetchClassesList();
      setClassDeletedNotifyTick(prev => prev + 1);

      if (!isMobile) {
        setIsClassEdit(false);
        onSelectClass(null);
      } else {
        let selectClass: TeacherGroup | null = null;
        const classrooms = filterdClassrooms.filter(classroom => classroom.id !== classId);
        if (classrooms.length > 0) {
          selectClass = classrooms[0];
        }
        onSelectClass(selectClass);
      }
    } catch (error) {
      console.log(error);
      commonUtils.showGlobalError({ i18nKey: 'error.somethingWentWrong' });
    } finally {
      setIsClassDeleting(false);
      setIsClassEdit(false);
    }
  };

  return {
    allClassrooms,
    filterdClassrooms,
    classConfig,
    activeGroupId,
    onCreateClass,
    createOrSaveClass,
    onSelectClass,
    classFilters,
    setClassFilters,
    classFilterFields,
    classCascadingFilters,
    hasAnyClassFilter,
    showClassCreatedModal,
    classCreatedWithoutStudentsNotifyTick,
    classDeletedNotifyTick,
    showExistingStudentsModal,
    onCloseClassCreatedModal,
    onCloseExistingStudentsModal,
    showCopyModal,
    setShowCopyModal,
    onCloseCopyModal,
    onShowCopyModal,
    onAbortAddClass,
    clearAddClassFlow,
    user,
    isSavingGroup: groupDetails.isSavingGroup,
    isCsvLoading,
    isCsvSending,
    setIsCsvLoading,
    csvFilesAdded,
    setCsvFilesAdded,
    showUploadCsvModal,
    addStudentsToNewGroup,
    showStudentsTableModal,
    onShowUploadCsvModal,
    onCloseUploadCsvModal,
    onShowStudentsTableModal,
    onBackStudentsTableModal,
    studentsData,
    setStudentsData,
    onCsvStudentsCreate,
    disableSubmitButton,
    setDisableSubmitButton,
    showNewStudentInput,
    setShowNewStudentInput,
    classStudentsExtraDataResponse,
    currentClassroom,
    isLoading,
    setIsLoading,
    fetchDataError,
    refreshStudentsData,
    studentsProgress,
    statistics,
    selectedStudent,
    isClassEdit,
    setIsClassEdit,
    searchValue,
    setSearchValue,
    onShowStudentModal,
    onHideStudentModal,
    isStudentDataLoading,
    showStudentDataModal,
    selectedStudentData,
    showStudentEditModal,
    onShowStudentEditModal,
    onCloseStudentEditModal,
    editStudent,
    showUnitData,
    onShowUnitData,
    onHideUnitData,
    selectedUnit,
    showCsvStudentsCreateError,
    setShowCsvStudentsCreateError,
    onStudentDelete,
    isStudentDeleting,
    showStudentDeleteError,
    setShowStudentDeleteError,
    isClassDeleting,
    showClassDeleteError,
    setShowClassDeleteError,
    onClassDelete,
    groupDetailsFromApi,
    isClassesListLoading,
    classesListError,
    filteredClassesReportRows,
    refetchClassesList,
    desktopClassesReport,
  };
};

export default UseMyClasses;
